import Aria from "../../util/Aria";
import { Timeout, addCSSRule, dateFormatter, decodeEntities, generateBackgroundImgUrl, getUrlParams, isIOS } from "../../util/Misc";
import { objToStyleMap } from "../../shoutui/utils";
import { Constants } from "../../model/Constants";
import {
    handleRichMediaAction,
    ifResonsiveHyperLinks,
    linksWithNuanceDatapass,
    linksWithNuanceResponseData,
    linksWithNuanceResponseText,
    logSelectedLink,
    renderRichMediaWidget
} from "./controller/BubbleController";
import BubbleType from "../../model/BubbleType";
import { agentbubbleRender } from "../templates/renders/agentbubble";
import shoutUIComponent from "../../../model/shoutUIComponent";

export interface TypeDef_AgentBubble extends shoutUIComponent {
    constants?: any;
    $app?: any;
    firstChild?: HTMLElement;
    classList?: DOMTokenList;
    clientHeight?: number;
    offsetTop?: number;
    udpateBackground: (A: Event) => void;
    onBubbleTouch: (A) => void;
    handleLinkClick: (A, B) => void;
    onBubbleClick: (A) => void;
    shouldIgnoreLinkClick: () => void;
    openLink: (A: HTMLAnchorElement, B: Event) => void;
    handleResponsiveHyperLinks: (A: HTMLAnchorElement, B: Event) => void;
    setRichObject: (A, B) => void;
    hideBottomInfo: () => void;
    hideTopInfo: () => void;
    hideTime: () => void;
    hideAlias: () => void;
    messageHasAsyncSurveyLink: (A: string) => void;
    setRichMediaAccessibilityFocus: (A: string, B, C) => void;
    announceAria: () => void;
    getType: () => number;
    setAccessibiltyFocus: () => void;
}

/**
 * Creates the agent-bubble custom element that is created for every agent message
 * @type {{template, onBubbleClick(*=): void, created(): void, tagName: string, udpateBackground(*=): void}}
 */
const AgentBubble: TypeDef_AgentBubble = {
    tagName: "agent-bubble",
    initialize(options) {
        initComponent(options);
    },
    renderer: agentbubbleRender,
    created() {
        this.ariaUtil = new Aria(this.$app);

        this.bubble = this.el.querySelector(".agent-bubble");

        // this.bubble.innerHTML = decodeEntities(this.prop.messagetext);
        if (this.prop.restore === "true" || this.$app.getRestoring()) {
            this.bubble.classList.add("enter");
        } else {
            if (Timeout.exists("agentTimer") && !Timeout.executed("agentTimer")) {
                Timeout.clear("agentTimer")();
            }

            Timeout.set("agentTimer", () => {
                this.bubble.classList.add("enter");
            }, 25);
            this.setAccessibiltyFocus();
        }

        if (this.backgroundImg) {
            this.udpateBackground(this.backgroundImg);
        }
    },
    udpateBackground(propObj) {
        const imgUrl = generateBackgroundImgUrl(propObj, this.$app);

        if (imgUrl) {
            this.bubble.style.backgroundImage = imgUrl;
        }
    },

    /**
     * Handler for bubble touch event
     * anchor element handling is deferred in click/touchend events
     * we have used touchstart event so that anchor handling is not deferred
     * @param {Event} event
     */
    onBubbleTouch(event) {
        const eventTarget = event.target;
        const { tagName = "", href = "", parentElement = { tagName: "" } } = eventTarget || {};
        const isAnchorTagHref = tagName.toLowerCase() === "a" && href;
        const isDataSurveyUrl = eventTarget?.hasAttribute(Constants.DATA_SURVEY_URL);
        const isParentAnchorTag = parentElement.tagName.toLowerCase() === "a";   
        /* used string "javascript" + ":" 
           because "javascript:" throws Script Error */
        const javascriptURI = "javascript" + ":";
        const isJavaScriptHref = href.startsWith(javascriptURI) || parentElement.href?.startsWith(javascriptURI);

        if (!isJavaScriptHref && !this.shouldIgnoreLinkClick() && (isAnchorTagHref || isDataSurveyUrl || isParentAnchorTag)) {            
            event.preventDefault();
        }
    },

    /**
     * Handler for bubble click event
     * @param {Event} event
     */
    onBubbleClick(event): any {
        const targetEl = event.target;
        const touches = event.changedTouches;

        // touches and moving should not fire the action
        if (touches) {
            const elem = document.elementFromPoint(touches[0].clientX, touches[0].clientY);

            if (elem != targetEl) {
                return false;
            }
        }

        if (targetEl.hasAttribute(Constants.DATA_SURVEY_URL)) {
            const url = targetEl.getAttribute(Constants.DATA_SURVEY_URL);
            const params = getUrlParams(url);
            this.$app.displayPostChatSurvey(params["surveyID"], null, params);
            event.preventDefault();

            if (isIOS()) {
                this.$app.getBridge().onVirtualKeyboardHidden();
            }
        } else if (this.prop.auto === "true" && !this.shouldIgnoreLinkClick()) {
            // VA links are send to SDKInstance for processing            
            this.$app.onVALinkClick(event);
        } else if (targetEl.tagName.toLowerCase() === "a") {
            this.handleLinkClick(targetEl, event);
        } else if (targetEl.parentElement.tagName.toLowerCase() == "a") {
            this.handleLinkClick(targetEl.parentElement, event);
        }

        targetEl.focus();
    },

    /**
     * @param {HTMLElement} anchorEl
     * @param {Event} event
     */
    handleLinkClick(anchorEl, event) {
        /*
         * should not pass the link opening request to IJSF if it contains CoBrowseMgr keyword or href is empty or null
         * Having Empty or null href will cause blank tab opened by IJSF
         */
        if (this.shouldIgnoreLinkClick() || !anchorEl.href) {
            return;
        }

        if (this.logLinks) {
            logSelectedLink(this.$app, this.logLinks, anchorEl.href);
        }

        if (ifResonsiveHyperLinks(anchorEl)) {
            this.handleResponsiveHyperLinks(anchorEl, event);
        } else {
            this.openLink(anchorEl, event);
        }
    },
    shouldIgnoreLinkClick() {
        const text = this.data.messageText;
        return text.includes("CoBrowseMgr") || text.includes("nuance-event");
    },
    openLink(anchorEl, event) {
        const hrefValue = anchorEl.getAttribute("href");
        const hrefDoesNotStartWithJsTag = !hrefValue.startsWith("javascript:");
        const hrefNotEmptyNotAnchorTag = (hrefValue !== "#" && hrefValue !== "");

        if (hrefDoesNotStartWithJsTag && hrefNotEmptyNotAnchorTag) {
            // Agent pushed links are send to Bootstrap for top window navigation
            this.$app.bridge.pushToFrameSet(anchorEl.href, anchorEl.target);
            event.preventDefault();
        }
    },

    /**
     * handle resposive hyperlink with nuance respose attributes
     * @param {HTMLAnchorElement} anchorEl - anchor element
     * @param {Event} event - passed event to handle on click
     */
    handleResponsiveHyperLinks(anchorEl, event) {
        const responseText = anchorEl.getAttribute(Constants.NUANCE_MESSAGE_TEXT);
        const responseData = anchorEl.getAttribute(Constants.NUANCE_MESSAGE_DATA);
        const responseDataPass = anchorEl.getAttribute(Constants.NUANCE_DATAPASS);
        const hrefValue = anchorEl.getAttribute("href");

        if (responseData && responseText) {
            linksWithNuanceResponseData(this.$app, responseText, responseData);
        } else if (responseText) {
            linksWithNuanceResponseText(this.$app, responseText);
        }

        if (responseDataPass) {
            linksWithNuanceDatapass(this.$app, responseDataPass);
        }

        if (hrefValue.startsWith("http")
        || hrefValue.startsWith("mailto:") 
        || hrefValue.startsWith("tel:")
        ) {
            this.openLink(anchorEl, event);
        }

        event.preventDefault();        
    },

    /**
     * creates and return the RichEngine instance
     * @param {Object} richData - rich widget json
     * @param {Message} message - Rich Widget message object
     * @return {RichMain}
     */
    setRichObject(richData, message) {
        if (window.RichMain) {
            this.richInstance = window.RichMain.instance;
            const richElement = document.createElement("div");
            let shouldLock = false;
            let shouldReport = this.richMediaReporting;
            const isRestore = this.prop.restore === "true";

            // should allow user to act is rich media is the last element when restoring
            if (isRestore && !message.isLast()) {
                shouldLock = typeof richData.lockView === "undefined" ? true : richData.lockView;
                shouldReport = false;
            }

            renderRichMediaWidget(this.richInstance, this.$app, richData,
                richElement, shouldLock, shouldReport, outputText => {
                    this.setRichMediaAccessibilityFocus(outputText, richData, message);
                });

            handleRichMediaAction(this.$app, this.richInstance, this.logLinks);

            if (this.namePos == Constants.WITHIN_BOTTOM || this.timePos == Constants.WITHIN_BOTTOM) {
                this.bubble.insertBefore(richElement, this.bubble.lastElementChild);
            } else {
                this.bubble.appendChild(richElement);
            }

            return this.richInstance;
        }
        return null;
    },
    hideBottomInfo() {
        if (this.gTime && this.timePos == Constants.WITHIN_BOTTOM) {
            this.hideTime();
        }

        if (this.gAlias && this.namePos == Constants.WITHIN_BOTTOM) {
            this.hideAlias();
        }
    },
    hideTopInfo() {
        if (this.gTime && this.timePos == Constants.WITHIN_TOP) {
            this.hideTime();
        }

        if (this.gAlias && this.namePos == Constants.WITHIN_TOP) {
            this.hideAlias();
        }
    },
    hideTime() {
        const timeSpan = this.bubble.querySelector(".msg-info-time span");

        if (timeSpan) {
            timeSpan.style.display = "none";
        }
    },
    hideAlias() {
        const nameSpan = this.bubble.querySelector(".msg-info-name span");

        if (nameSpan) {
            nameSpan.style.display = "none";
        }
    },

    /**
     * Determines whether a message is an async survey message
     *
     * The DATA_SURVEY_URL attribute is added by:
     * com.inq.flash.client.control.CMSDKAppController.skinControl.prepareAsyncSurveyMessage
     *
     * It replaces the href attribute that is defined in the survey link in portal for the client
     *
     * @param   {string} messageText    A message sent by the agent (this.data.messageText)
     * @returns {boolean}               Boolean indicating whether the message includes an async
     *                                  survey link
     */
    messageHasAsyncSurveyLink(messageText) {
        const isAsyncChat = this.$app.isAsyncChat;
        const messageHasSurveyLink = messageText.includes(Constants.DATA_SURVEY_URL);
        return isAsyncChat && messageHasSurveyLink;
    },
    setRichMediaAccessibilityFocus(outputText, richData, message) {
        let announce = false;

        if (!message.getIsRestore()) {
            announce = this.$app.focusMgr.handleFocusSetting(this.bubble, richData.focusSettingMode);
        }

        if (announce || message.isLast()) {
            this.ariaUtil.announceRichDataMessage(outputText);
        }
    },

    /**
     * Accessibility
     *
     * When the chat is resolved by agent, the customer can receive a message with a post-chat
     * survey link. Our aria-reader will read out that survey message, but a person who uses
     * accessibility software will have to navigate through the chat until they can reach the
     * survey link to be able to activate it. This feature makes it so when we receive a survey
     * link we automatically focus it, in order to avoid a lot of navigation by the user.
     * When not a survey message, where the focus goes can be controlled through a configuration
     */
    setAccessibiltyFocus() {
        if (this.messageHasAsyncSurveyLink(this.data.messageText)) {
            this.$app.focusMgr.focusLinkTag(this.bubble);
        } else {
            if (this.data.richMedia) {
                return;
            }

            let announce = false;

            if (parseInt(this.prop.type) === BubbleType.OPENER_MESSAGE) {
                announce = this.$app.focusMgr.handleFocusWhenChatLaunched(this.bubble, this.$app.focusOnOpen);
            } else {
                announce = this.$app.focusMgr.handleFocusSetting(this.bubble,
                    this.focusSettingMode);
            }

            if (announce) {
                this.announceAria();
            }
        }
    },
    announceAria() {
        this.ariaUtil.announceBubbleText(this.prop.messagetext, this.prop.name);
    },
    getType(): number {
        return parseInt(this.prop.type, 10);
    }

};

/**
 * @export AgentBubble
 * @param {CIApp} appInstance
 * @return {{template, onBubbleClick, (*=): void, created, (): void, tagName: string, udpateBackground, (*=): void}}
 */
export default appInstance => {
    const constObj: any = {};
    AgentBubble.$app = appInstance;
    AgentBubble.constants = constObj;
    return AgentBubble;
};

const initComponent = (options: any) => {
    const appInstance = options.$app;
    const config = appInstance.getUIConfig();
    const constObj: any = options.constants;
    const dt = appInstance.getSDKInstance().getDeviceType();

    if (config.transcriptConfig) {
        const tConfig = config.transcriptConfig;
        constObj.style = tConfig.agentBubbleStyles;
        constObj.nameStyle = tConfig.agentAliasStyles;
        constObj.timeStyle = tConfig.dateTimeStyles;
        constObj.backgroundImg = tConfig.agentBackgroundImageConfig;

        if (tConfig.agentBubbleAfterStyles) {
            const after = objToStyleMap(tConfig.agentBubbleAfterStyles);
            addCSSRule(document.styleSheets[0], ".agent-bubble:after", after);
        }

        if (tConfig.agentBubbleBeforeStyles) {
            const before = objToStyleMap(tConfig.agentBubbleBeforeStyles);
            addCSSRule(document.styleSheets[0], ".agent-bubble:before", before);
        }
    }

    const appConfig = config.appConfigProps;

    if (appConfig) {
        constObj.hugging = appConfig.enableHugging;
        constObj.showName = appConfig.displayAgentAlias;
        constObj.namePos = appConfig.agentAliasPosition;
        constObj.showTime = appConfig.displayDateAndTime;
        constObj.timePos = appConfig.dateTimePosition;
        constObj.gTime = appConfig.enableMsgGroupingDateTime && appConfig.enableMsgGrouping;
        constObj.gAlias = appConfig.enableMsgGroupingAgentAlias && appConfig.enableMsgGrouping;
        constObj.richMediaReporting = appConfig.enableRichMediaReporting;
        constObj.logLinks = appConfig.logSelectedLinks;
        constObj.focusSettingMode = appInstance.focusOnAgentMessage;
    }

    options.data = {
        messageText: state => decodeEntities(state.prop.messagetext || ""),
        timeText: state => dateFormatter(parseInt(state.prop.timestamp, 10), appConfig.timeStampFormat || "HH:mm"),
        richMedia: state => state.prop.richmedia === "true",
    };

    constObj.isMobile = dt == "Phone" || dt == "Tablet";
};
